/**
 * Apply site brand to document `<title>` and `<meta name="description">`.
 * Title: `{pageTitle} | {siteName}` when both exist, else site name alone.
 * Meta description: localized site slogan.
 */
export function applySiteBrandHead({ siteName = '', siteSlogan = '', pageTitle = '' } = {}) {
  if (typeof document === 'undefined') return;

  const name = String(siteName || '').trim();
  const slogan = String(siteSlogan || '').trim();
  const page = String(pageTitle || '').trim();

  if (name || page) {
    document.title = page && name ? `${page} | ${name}` : name || page;
  }

  let meta = document.querySelector('meta[name="description"]');
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', 'description');
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', slogan);
}

/**
 * True when SSG (or similar) already embedded published property brand in initial state.
 */
export function hasEmbeddedPropertyBrand(initialState) {
  const p = initialState?.property;
  if (!p || typeof p !== 'object') return false;
  return Boolean(String(p.siteName || '').trim() || String(p.siteSlogan || '').trim());
}

/** True when embedded brand exists but per-locale siteSlogan is missing (needs API refresh). */
export function needsPropertyBrandRefresh(initialState) {
  const p = initialState?.property;
  if (!p || typeof p !== 'object') return true;
  return !String(p.siteSlogan || '').trim();
}

/** Prefer primary locale brand fields; when empty, use fallback locale (e.g. DEFAULT_LOCALE). */
export function mergeSiteBrand(primary, fallback) {
  const pick = (a, b) => {
    const x = a != null ? String(a).trim() : '';
    if (x) return x;
    return b != null ? String(b).trim() : '';
  };
  return {
    siteName: pick(primary?.siteName, fallback?.siteName),
    siteSlogan: pick(primary?.siteSlogan, fallback?.siteSlogan)
  };
}
